import type {
  AchievementCategory,
  AchievementTier,
  AchievementWithState,
  BadgeGlyphId,
} from '../types/engagement'
import type { FeatureFlagKeyType } from '../types/feature-flags'

/**
 * Lifetime counters an achievement can be judged on. The API builds these from
 * the engagement signal ledger (counts) and time entries (hours).
 */
export type LifetimeMetric =
  | 'tasksCompleted'
  | 'timeEntriesLogged'
  | 'hoursLogged'
  | 'ticketsResolved'
  | 'dealsAdvanced'
  | 'dealsWon'
  | 'focusPlansDone'
  | 'goalsAchieved'
  | 'earlyBirdDays'

/** One-off conditions computed by the API for the triggering signal. */
export type AchievementFlag = 'inbox_zero' | 'focus_week' | 'comeback'

export type AchievementRule =
  | { kind: 'counter'; metric: LifetimeMetric; threshold: number }
  | { kind: 'streak'; days: number }
  | { kind: 'flag'; flag: AchievementFlag }

export interface AchievementDefinition {
  /** Stable id. Never rename: it is stored on user_achievements rows. */
  key: string
  name: string
  description: string
  tier: AchievementTier
  category: AchievementCategory
  glyph: BadgeGlyphId
  rule: AchievementRule
  /** Hide (and never award) the badge while this module is off for the org. */
  featureFlag?: FeatureFlagKeyType
}

export interface AchievementContext {
  lifetime: Partial<Record<LifetimeMetric, number>>
  /** max(current streak, longest streak on record). */
  bestStreak: number
  flags: Partial<Record<AchievementFlag, boolean>>
}

const counter = (
  metric: LifetimeMetric,
  threshold: number,
): AchievementRule => ({ kind: 'counter', metric, threshold })

/**
 * The work-account badge catalog. Order is display order within a category.
 * Copy budget: names are 3 words or fewer, descriptions 8 words or fewer.
 */
export const ACHIEVEMENT_CATALOG: readonly AchievementDefinition[] =
  Object.freeze(
    [
      // ---- Getting started ----
      {
        key: 'first_task_done',
        name: 'First check',
        description: 'Complete your first task',
        tier: 'bronze',
        category: 'onboarding',
        glyph: 'check-single',
        rule: counter('tasksCompleted', 1),
        featureFlag: 'TASKS',
      },
      {
        key: 'first_time_entry',
        name: 'On the clock',
        description: 'Log your first time entry',
        tier: 'bronze',
        category: 'onboarding',
        glyph: 'clock',
        rule: counter('timeEntriesLogged', 1),
        featureFlag: 'TIME_TRACKING',
      },
      {
        key: 'first_ticket_resolved',
        name: 'First save',
        description: 'Resolve your first ticket',
        tier: 'bronze',
        category: 'onboarding',
        glyph: 'shield',
        rule: counter('ticketsResolved', 1),
        featureFlag: 'TICKETS',
      },

      // ---- Streaks ----
      {
        key: 'streak_3',
        name: 'Warming up',
        description: 'Three working days in a row',
        tier: 'bronze',
        category: 'streaks',
        glyph: 'flame',
        rule: { kind: 'streak', days: 3 },
      },
      {
        key: 'streak_7',
        name: 'On a roll',
        description: 'Seven working days in a row',
        tier: 'silver',
        category: 'streaks',
        glyph: 'flame',
        rule: { kind: 'streak', days: 7 },
      },
      {
        key: 'streak_14',
        name: 'Steady hands',
        description: 'Fourteen working days in a row',
        tier: 'silver',
        category: 'streaks',
        glyph: 'flame',
        rule: { kind: 'streak', days: 14 },
      },
      {
        key: 'streak_30',
        name: 'Unstoppable',
        description: 'Thirty working days in a row',
        tier: 'gold',
        category: 'streaks',
        glyph: 'flame',
        rule: { kind: 'streak', days: 30 },
      },
      {
        key: 'streak_100',
        name: 'Hundred club',
        description: 'One hundred working days in a row',
        tier: 'gold',
        category: 'streaks',
        glyph: 'flame-crown',
        rule: { kind: 'streak', days: 100 },
      },

      // ---- Tasks ----
      {
        key: 'tasks_10',
        name: 'Getting going',
        description: 'Complete 10 tasks',
        tier: 'bronze',
        category: 'tasks',
        glyph: 'check-stack',
        rule: counter('tasksCompleted', 10),
        featureFlag: 'TASKS',
      },
      {
        key: 'tasks_50',
        name: 'Fifty done',
        description: 'Complete 50 tasks',
        tier: 'silver',
        category: 'tasks',
        glyph: 'check-stack',
        rule: counter('tasksCompleted', 50),
        featureFlag: 'TASKS',
      },
      {
        key: 'tasks_250',
        name: 'Heavy lifter',
        description: 'Complete 250 tasks',
        tier: 'gold',
        category: 'tasks',
        glyph: 'layers',
        rule: counter('tasksCompleted', 250),
        featureFlag: 'TASKS',
      },
      {
        key: 'tasks_1000',
        name: 'Summit',
        description: 'Complete 1,000 tasks',
        tier: 'gold',
        category: 'tasks',
        glyph: 'mountain',
        rule: counter('tasksCompleted', 1000),
        featureFlag: 'TASKS',
      },

      // ---- Time ----
      {
        key: 'hours_100',
        name: 'Hundred hours',
        description: 'Log 100 hours of work',
        tier: 'silver',
        category: 'time',
        glyph: 'clock-stack',
        rule: counter('hoursLogged', 100),
        featureFlag: 'TIME_TRACKING',
      },
      {
        key: 'hours_500',
        name: 'Deep work',
        description: 'Log 500 hours of work',
        tier: 'gold',
        category: 'time',
        glyph: 'clock-stack',
        rule: counter('hoursLogged', 500),
        featureFlag: 'TIME_TRACKING',
      },

      // ---- Tickets ----
      {
        key: 'inbox_zero',
        name: 'Inbox zero',
        description: 'Resolve your last open ticket',
        tier: 'silver',
        category: 'tickets',
        glyph: 'inbox-zero',
        rule: { kind: 'flag', flag: 'inbox_zero' },
        featureFlag: 'TICKETS',
      },
      {
        key: 'tickets_100',
        name: 'Help desk hero',
        description: 'Resolve 100 tickets',
        tier: 'gold',
        category: 'tickets',
        glyph: 'shield',
        rule: counter('ticketsResolved', 100),
        featureFlag: 'TICKETS',
      },

      // ---- Deals ----
      {
        key: 'deal_closer_1',
        name: 'Closer',
        description: 'Win your first deal',
        tier: 'bronze',
        category: 'deals',
        glyph: 'handshake',
        rule: counter('dealsWon', 1),
        featureFlag: 'DEALS',
      },
      {
        key: 'pipeline_mover_25',
        name: 'Pipeline mover',
        description: 'Move deals forward 25 times',
        tier: 'silver',
        category: 'deals',
        glyph: 'rocket',
        rule: counter('dealsAdvanced', 25),
        featureFlag: 'DEALS',
      },
      {
        key: 'deal_closer_10',
        name: 'Rainmaker',
        description: 'Win 10 deals',
        tier: 'gold',
        category: 'deals',
        glyph: 'trophy',
        rule: counter('dealsWon', 10),
        featureFlag: 'DEALS',
      },

      // ---- Focus ----
      {
        key: 'focus_first',
        name: 'Plan made good',
        description: 'Finish every block in a day plan',
        tier: 'bronze',
        category: 'focus',
        glyph: 'target',
        rule: counter('focusPlansDone', 1),
        featureFlag: 'FOCUS_MODE',
      },
      {
        key: 'focus_week',
        name: 'Clear week',
        description: 'Finish five day plans in one week',
        tier: 'silver',
        category: 'focus',
        glyph: 'compass',
        rule: { kind: 'flag', flag: 'focus_week' },
        featureFlag: 'FOCUS_MODE',
      },

      // ---- Goals ----
      {
        key: 'goal_hit_1',
        name: 'Goal getter',
        description: 'Hit your first weekly goal',
        tier: 'bronze',
        category: 'goals',
        glyph: 'flag',
        rule: counter('goalsAchieved', 1),
      },
      {
        key: 'goal_hit_5',
        name: 'Five for five',
        description: 'Hit five weekly goals',
        tier: 'silver',
        category: 'goals',
        glyph: 'medal',
        rule: counter('goalsAchieved', 5),
      },
      {
        key: 'goal_hit_20',
        name: 'Goal machine',
        description: 'Hit twenty weekly goals',
        tier: 'gold',
        category: 'goals',
        glyph: 'trophy',
        rule: counter('goalsAchieved', 20),
      },

      // ---- Rhythm ----
      {
        key: 'early_bird',
        name: 'Early bird',
        description: 'Finish a task before 9am, five days',
        tier: 'silver',
        category: 'rhythm',
        glyph: 'sunrise',
        rule: counter('earlyBirdDays', 5),
        featureFlag: 'TASKS',
      },
      {
        key: 'comeback',
        name: 'Welcome back',
        description: 'Return after two weeks away',
        tier: 'bronze',
        category: 'rhythm',
        glyph: 'boomerang',
        rule: { kind: 'flag', flag: 'comeback' },
      },
    ].map(def => Object.freeze(def as AchievementDefinition)),
  )

export type AchievementKey = (typeof ACHIEVEMENT_CATALOG)[number]['key']

const BY_KEY = new Map(ACHIEVEMENT_CATALOG.map(def => [def.key, def]))

export function getAchievementDefinition(
  key: string,
): AchievementDefinition | undefined {
  return BY_KEY.get(key)
}

/** Display order for category sections. */
export const ACHIEVEMENT_CATEGORY_ORDER: readonly {
  category: AchievementCategory
  label: string
}[] = [
  { category: 'onboarding', label: 'Getting started' },
  { category: 'streaks', label: 'Streaks' },
  { category: 'tasks', label: 'Tasks' },
  { category: 'time', label: 'Time' },
  { category: 'tickets', label: 'Tickets' },
  { category: 'deals', label: 'Deals' },
  { category: 'focus', label: 'Focus' },
  { category: 'goals', label: 'Goals' },
  { category: 'rhythm', label: 'Rhythm' },
]

/** True when the definition's rule is satisfied by the context. */
export function isAchievementMet(
  def: AchievementDefinition,
  ctx: AchievementContext,
): boolean {
  const rule = def.rule
  switch (rule.kind) {
    case 'counter':
      return (ctx.lifetime[rule.metric] ?? 0) >= rule.threshold
    case 'streak':
      return ctx.bestStreak >= rule.days
    case 'flag':
      return ctx.flags[rule.flag] === true
  }
}

/**
 * Keys of every achievement the context satisfies, optionally limited to the
 * modules enabled for the org. Pure and deterministic: the API inserts the
 * result with ON CONFLICT DO NOTHING, so re-evaluating never re-awards.
 */
export function evaluateAchievements(
  ctx: AchievementContext,
  isFlagEnabled: (flag: FeatureFlagKeyType) => boolean = () => true,
): string[] {
  return ACHIEVEMENT_CATALOG.filter(
    def =>
      (!def.featureFlag || isFlagEnabled(def.featureFlag)) &&
      isAchievementMet(def, ctx),
  ).map(def => def.key)
}

/** Progress toward a locked badge, or null when it has no meaningful bar. */
export function achievementProgress(
  def: AchievementDefinition,
  ctx: AchievementContext,
): { current: number; target: number } | null {
  const rule = def.rule
  if (rule.kind === 'counter') {
    if (rule.threshold <= 1) return null
    const value = Math.floor(ctx.lifetime[rule.metric] ?? 0)
    return { current: Math.min(value, rule.threshold), target: rule.threshold }
  }
  if (rule.kind === 'streak') {
    return { current: Math.min(ctx.bestStreak, rule.days), target: rule.days }
  }
  return null
}

/** Merge the catalog with a user's earned rows for display. */
export function buildAchievementStates(
  earned: ReadonlyMap<string, string>,
  ctx: AchievementContext | null,
  isFlagEnabled: (flag: FeatureFlagKeyType) => boolean = () => true,
): AchievementWithState[] {
  return ACHIEVEMENT_CATALOG.filter(
    def => !def.featureFlag || isFlagEnabled(def.featureFlag),
  ).map(def => {
    const earnedAt = earned.get(def.key) ?? null
    return {
      key: def.key,
      name: def.name,
      description: def.description,
      tier: def.tier,
      category: def.category,
      glyph: def.glyph,
      earnedAt,
      progress: earnedAt || !ctx ? null : achievementProgress(def, ctx),
    }
  })
}
